/**
 * formatDownloadedAt — pure Helper für „zuletzt heruntergeladen" in
 * „Meine Transfers".
 *
 * Warum ein eigener Helper (Spec-Eval R1-F3): das Zielformat `9. Aug. 14:22`
 * produziert KEINER der bestehenden Helfer. `formatExpiry`/`formatDateDE`
 * liefern `DD.MM.YYYY` (kein Uhrzeit-Anteil), `formatRelative` ist
 * past-only-relativ („vor 3 Tagen") — für „wann genau war der letzte Zugriff"
 * ist relativ zu unscharf. Ohne dedizierten Helper hätte nichts die tatsächliche
 * Ausgabe gegen das Mockup validiert.
 *
 * Format:
 *   gleiches Kalenderjahr wie `now`:  `9. Aug. 14:22`
 *   anderes Kalenderjahr:             `9. Aug. 2025, 14:22`
 *   null:                             `''`
 *
 * Das Jahr erscheint nur bei Abweichung, ist dort aber nicht optional: die
 * Transfer-Liste zeigt bis zu 200 Einträge inklusive abgelaufener — ohne Jahr
 * wäre ein 14 Monate alter Download nicht von einem frischen zu unterscheiden.
 *
 * Tag ohne Zero-Pad (`9.`), Uhrzeit mit (`09:05`) — deutsche Lesekonvention.
 *
 * @param iso ISO-8601-Timestamp oder null
 * @param now Reference-time in ms (default `Date.now()`) — für deterministische Tests
 */

/**
 * Deutsche Monats-Kurzformen. Die vier kurzen Namen (März, Mai, Juni, Juli)
 * werden nicht abgekürzt und tragen deshalb KEINEN Punkt — eine Abkürzung, die
 * nichts kürzt, ist im Deutschen falsch gesetzt. Bewusst eine eigene Tabelle
 * statt `toLocaleDateString('de-DE')`: Intl liefert je nach Node-/Browser-ICU
 * unterschiedliche Varianten (mit/ohne Punkt, „Sept." vs. „Sep."), was den
 * wörtlichen String-Assertions der Tests den Boden entzieht.
 */
const MONTHS_DE = [
  'Jan.',
  'Feb.',
  'März',
  'Apr.',
  'Mai',
  'Juni',
  'Juli',
  'Aug.',
  'Sep.',
  'Okt.',
  'Nov.',
  'Dez.',
] as const;

export function formatDownloadedAt(iso: string | null, now = Date.now()): string {
  if (iso === null) return '';

  const date = new Date(iso);
  const ms = date.getTime();
  // Defensiv: ein unparsebarer String soll die Liste nicht mit „NaN. undefined"
  // dekorieren — lieber gar kein Segment rendern (Aufrufer prüft auf '').
  if (Number.isNaN(ms)) return '';

  const day = date.getDate();
  const month = MONTHS_DE[date.getMonth()];
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  const year = date.getFullYear();
  const currentYear = new Date(now).getFullYear();

  return year === currentYear
    ? `${day}. ${month} ${hours}:${minutes}`
    : `${day}. ${month} ${year}, ${hours}:${minutes}`;
}
